/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import { useEffect, useState } from 'react';
import moment from 'moment';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SwipeableViews from 'react-swipeable-views';
import {
  Modal,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@components/Modal';
import { AxiosResponse } from 'axios';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { useAtom } from 'jotai';
import { BusinessProfile } from '@views/Profile/Profile';
import { Mapbox } from '@views/WarehouseDetails/Mapbox';
import {
  CheckIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  EyeSlashIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { MapPinIcon } from '@heroicons/react/20/solid';
import { formatPrice, isSameBusiness } from '@utils';
import { AgreeToWarehouseTandCsModal } from '@components/AgreeToWarehouseTandCsModal';
import { EngageLspModal } from '@components/EngageLspModal';
import {
  BUSINESS_PROFILE_GET,
  MATCH_LIST,
  WAREHOUSE_DELIST,
  WAREHOUSE_GET,
  WAREHOUSE_IMAGES_URL,
  WAREHOUSE_SEARCH_GET,
} from '../../constants';
import { Button } from '../../tailwindComponents/Button';
import { useAuthHttp } from '../../hooks';
import { UserRoleService } from '../../services';
import { useAuth } from '../../context';
import { businessProfileAtom, matchAtom } from '../../store/jotai';
import {
  STORAGE_CAPABILITIES,
  MATERIAL_HANDLING_EQUIPMENT,
  SAFETY_AND_SECURITY,
  WAREHOUSE_OPERATIONS,
  FULFILMENT_SERVICES,
  ADDITIONAL_SERVICES,
  ACCREDITATIONS,
} from './constants';
import ukwaLogo from '../../../public/images/UKWA_Member_Logo.png';
import { FacilityListProps } from './types';
import { Subheader } from '../../tailwindComponents/Subheader';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';

export type WarehouseMatch = {
  match: Match;
  warehouse: Warehouse;
};

export type WarehouseMatchList = {
  matches: WarehouseMatch[];
};

export type WarehouseData = {
  warehouse: Warehouse;
};

export type Warehouse = {
  warehouseId: string;
  createdAt: string;
  userBusinessName: string;
  userId: string;
  userBusinessId: string;
  isLspFoundingPartner?: boolean;
  name: string;
  description: string;
  address: string;
  city: string;
  location: {
    lat: number;
    lon: number;
  };
  temperatureControlTypes: string[];
  temperatureMonitoringCapeabilities: string[];
  loadingDocks: number;
  isOffDock: boolean;
  isQuay: boolean;
  hasOnsiteOffice: boolean;
  onsiteOfficePhoneNumber: {
    countryCode: 'string';
    countryNumberPrefix: 'string';
    number: 'string';
  };
  images: [
    {
      id: string;
      title: string;
      originalFileName: string;
      remoteName: string;
      imageLocation: string;
    }
  ];
  maxStorageCapacity: number;
  maxStorageCapacityAvailable: number;
  currentlyAvailableCapacity: number;
  minStorageCapacityThreshold: number;
  minStorageTimeThreshold: number;
  storageTimeUnitOfMeasure: string;
  storageCapacityUnitOfMeasure: string;
  currency: string;
  movementPrice: number;
  avgPricePerManHour: number;
  businessName: string;
  businessNumber: string;
  businessAddress: string;
  businessIndustry: string;
  businessWebsite: string;
  businessContactName: string;
  businessContactEmail: string;
  businessContactPhoneNumber: string;
  numberOfShifts: number;
  minHeadcount: number;
  maxHeadcount: number;
  standardBusinessHoursType: string;
  hasFlexibleBusinessHours: boolean;
  customBusinessHours: {
    day: string;
    open: string;
    close: string;
  }[];
  minEavesHeight: {
    quantity: number;
    unitOfMeasure: string;
  };
  constructionYear: number;
  grossInternalArea: {
    quantity: number;
    unitOfMeasure: string;
  };
  mezzanineArea: {
    quantity: number;
    unitOfMeasure: string;
  };
  isRackingAvailable: boolean;
  individualRackDepth: number;
  individualRackHeight: number;
  individualRackWidth: number;
  totalRackHeight: number;
  storageCapabilities: string[];
  materialHandlingEquipment: string[];
  yardSpace: {
    quantity: number;
    unitOfMeasure: string;
  };
  vehicleAccessHeight: {
    quantity: number;
    unitOfMeasure: string;
  };
  leveledLoadingDocks: number;
  doubleHeightLoadingDocks: number;
  carParkingSpaces: number;
  lorryParkingSpaces: number;
  sectorExperience: string[];
  noteableCustomers: string[];
  warehouseOperations: string[];
  fulfilmentServices: string[];
  fleet: {
    type: string;
    count: number;
  }[];
  additionalServices: string[];
  safetyAndSecurity: string[];
  systemsAvailable: string[];
  accreditationsAndMemberships: string[];
  accreditationOther?: string | null;
  vehicleLoadingTimeWindows: {
    day: string;
    open: string;
    close: string;
  }[];
  dispatchCutoffTime: string;
  modifiedAt: string;
  palletStoragePricePerWeek: number;
  squareFeetStoragePricePerWeek: number;
  squareMetreStoragePricePerWeek: number;
  palletReceiptAndPutAwayPrice: number;
  palletPickingAndDespatchPrice: number;
  palletLayerPickingAndDespatchPrice: number;
  casePickingAndDespatchPrice: number;
  subsequentCasePickingAndDespatchPrice: number;
  itemPickingAndDespatchPrice: number;
  subsequentItemPickingAndDespatchPrice: number;
  containerHandlingPrice: number;
  demurragePricePerDay: number;
  palletReturnsPrice: number;
  itemReturnsPrice: number;
  caseReturnsPrice: number;
  provisionOfPalletPrice: number;
  auxiliaryServicePricePerManHour: number;
  additionalSurchargesFlatFee: number;
  extraInfo?: string | null;
  isDelisted: boolean;
  isFull: boolean;
  isLspBusinessDeactivated: boolean;
  isDraft: boolean;
  isLive: boolean;
  status: string;
  authenticity: {
    authenticityLevel: string;
    wasSuccessfulAudit: boolean;
  };
  isPartialProfile: boolean;
};

type Fleet =
  | 'VanLessThan1AndHalft'
  | 'LgvLessThan3AndHalft'
  | 'TwoAxleRigidLessThan7AndHalf5t'
  | 'TwoAxleRigid18t'
  | 'TwoAxleRigidLessThan26t'
  | 'MultiAxleArticMoreThan26t'
  | 'SpecialOrOther';

type ServiceRate = {
  originalValue: number;
  shipperProposedValue: number;
  lspProposedValue: number;
  acceptedValue: number;
};

export type Match = {
  id: string;
  warehouseId: string;
  shipperId: string;
  desiredStartDate: string;
  averageStorageQuantityPerWeek: number;
  estimatedDurationInMonths: number;
  averageNumberOfInboundShipmentsPerWeek: number;
  averageNumberOfOutboundShipmentsPerWeek: number;
  shipperIndustry: string;
  requestedServices: string[];
  unavailableServices: string[];
  matchStatus: string;
  billingCycleFrequency: string;
  paymentTermsInDays: number;
  originalRates: {
    storagePricePerWeek: number;
    palletReceiptAndPutAwayPrice: number;
    palletPickingAndDespatchPrice: number;
    palletLayerPickingAndDespatchPrice: number;
    casePickingAndDespatchPrice: number;
    subsequentCasePickingAndDespatchPrice: number;
    itemPickingAndDespatchPrice: number;
    subsequentItemPickingAndDespatchPrice: number;
    containerHandlingPrice: number;
    demurragePricePerDay: number;
    palletReturnsPrice: number;
    itemReturnsPrice: number;
    caseReturnsPrice: number;
    auxiliaryServicePricePerManHour: number;
  };
  serviceRates: {
    storagePricePerWeek: ServiceRate;
    palletReceiptAndPutAwayPrice: ServiceRate;
    palletPickingAndDespatchPrice: ServiceRate;
    palletLayerPickingAndDespatchPrice: ServiceRate;
    casePickingAndDespatchPrice: ServiceRate;
    subsequentCasePickingAndDespatchPrice: ServiceRate;
    itemPickingAndDespatchPrice: ServiceRate;
    subsequentItemPickingAndDespatchPrice: ServiceRate;
    containerHandlingPrice: ServiceRate;
    demurragePricePerDay: ServiceRate;
    palletReturnsPrice: ServiceRate;
    itemReturnsPrice: ServiceRate;
    caseReturnsPrice: ServiceRate;
    auxiliaryServicePricePerManHour: ServiceRate;
  };
  volumeDiscounts: {
    id: string;
    acceptedValue: number;
    lspProposedDiscountPercentage: number;
    minTotalMonthlyTurnover: number;
    maxTotalMonthlyTurnover: number;
    shipperProposedDiscountPercentage: number;
  }[];
  isActionRequiredByLsp: boolean;
  isActionRequiredByShipper: boolean;
  userBusinessName: string;
  userBusinessId?: string;
  lspBusinessId?: string;
  lspBusinessName?: string;
  warehouseName?: string;
  acceptedAgreement?: AcceptedAgreement | null;
  amendedAt?: string | null;
};

export type AcceptedAgreement = {
  termsVersion?: string | null;
  acceptedAt: string;
  acceptedByUserId: string;
  acceptedByName?: string | null;
  acceptedForBusinessId: string;
  isAcceptedByFloxSupport: boolean;
};

function DataField({
  name,
  value,
}: {
  name: string;
  value?: string | React.ReactNode;
}) {
  return (
    <div className='flex flex-col items-start font-inter h-full justify-center'>
      <span className='text-gray-900 text-base font-medium mb-1'>
        {value ?? 'N/A'}
      </span>
      <span className='text-gray-400 text-sm'>{name}</span>
    </div>
  );
}

function DayHours({ day, hours }: { day: string; hours: string }) {
  const isClosed = hours === '00:00 - 00:00';
  return (
    <div className='flex items-center font-inter w-full min-w-[320px]'>
      <span className='text-sm font-normal text-gray-500'>{day}</span>
      <span className='flex-grow border-t border-dotted border-gray-300 mx-4' />
      <span className='text-sm font-normal text-gray-900'>
        {isClosed ? 'closed' : hours}
      </span>
    </div>
  );
}

function FacilityList({ title, items, available }: FacilityListProps) {
  return (
    <div className='w-full'>
      <div className='font-semibold text-base mb-4 text-gray-900'>{title}</div>
      <ul className='flex flex-col gap-3'>
        {items.map((item) => (
          <li key={item} className='flex items-center gap-2'>
            <span className='flex-shrink-0'>
              {available.includes(item) ? (
                <CheckIcon className='h-5 w-5 text-black' />
              ) : (
                <XMarkIcon className='h-5 w-5 text-gray-300' />
              )}
            </span>
            <span
              className={
                available.includes(item) ? 'text-gray-900' : 'text-gray-400'
              }
            >
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
export function WarehouseDetails() {
  const authHttp = useAuthHttp();
  const navigate = useNavigate();
  const [isFetchingWarehouse, setIsFetchingWarehouse] = useState(false);
  const [warehouse, setWarehouse] = useState<Warehouse>();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { isWarehouseManager, isWarehouseClient } = UserRoleService();
  const [isDelistWarehouseModalOpen, setIsDelistWarehouseModalOpen] =
    useState(false);
  const [isDelistWarehouseErrorShowing, setIsDelistWarehouseErrorShowing] =
    useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isNoBusinessProfileOpen, setIsNoBusinessProfileOpen] = useState(false);
  const [match, setMatch] = useAtom(matchAtom);
  const [businessProfile, setBusinessProfile] = useAtom(businessProfileAtom);
  const [isAgreeToWarehouseTandCsOpen, setIsAgreeToWarehouseTandCsOpen] =
    useState(false);
  const [isEngageLspModalOpen, setIsEngageLspModalOpen] = useState(false);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepChange = (step: number) => {
    setActiveStep(step);
  };

  const isContacting = searchParams.get('contact') || '';
  const email = searchParams.get('email') || '';
  const firstname = searchParams.get('firstname') || '';
  const lastname = searchParams.get('lastname') || '';

  const fetchWarehouse = async (isStillOnThisWarehouse: () => boolean) => {
    setIsFetchingWarehouse(true);
    try {
      const { data }: AxiosResponse<WarehouseData> = await authHttp.get(
        isWarehouseManager(user)
          ? WAREHOUSE_GET
          : `${WAREHOUSE_SEARCH_GET}/${id}`,
        {
          params: {
            warehouseId: id,
          },
        }
      );
      if (isStillOnThisWarehouse()) setWarehouse(data.warehouse);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      if (isStillOnThisWarehouse()) setIsFetchingWarehouse(false);
    }
  };

  const fetchWarehouseShortlist = async (
    isStillOnThisWarehouse: () => boolean
  ) => {
    try {
      const { data } = await authHttp.post<WarehouseMatchList>(
        MATCH_LIST,
        {
          warehouseId: warehouse?.warehouseId,
          matchId: null,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (data.matches.length > 0) {
        const negotiationMatch = data.matches.find(
          // eslint-disable-next-line @typescript-eslint/no-shadow
          (match) => match.match.matchStatus !== 'Rejected'
        );

        if (negotiationMatch && isStillOnThisWarehouse()) {
          setMatch(negotiationMatch.match);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const fetchBusinessProfile = async (
    isStillOnThisWarehouse: () => boolean
  ) => {
    if (!user) return;
    setIsFetchingWarehouse(true);
    try {
      const { data }: AxiosResponse<BusinessProfile> = await authHttp.get(
        BUSINESS_PROFILE_GET
      );

      if (data && isStillOnThisWarehouse()) {
        setBusinessProfile(data.businessProfile);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      if (isStillOnThisWarehouse()) setIsFetchingWarehouse(false);
    }
  };

  const delistWarehouse = async () => {
    setIsDelistWarehouseErrorShowing(false);
    try {
      const { data } = await authHttp.post(
        WAREHOUSE_DELIST,
        {
          warehouseId: warehouse?.warehouseId,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (data) {
        setWarehouse(data.warehouse);
        setIsDelistWarehouseModalOpen(false);
      }
    } catch (error) {
      console.error(error);
      setIsDelistWarehouseErrorShowing(true);
    }
  };

  useEffect(() => {
    let isStillOnThisWarehouse = true;
    setActiveStep(0);
    setWarehouse(undefined);
    setMatch(null);
    (async () => fetchWarehouse(() => isStillOnThisWarehouse))();
    (async () => fetchBusinessProfile(() => isStillOnThisWarehouse))();
    return () => {
      isStillOnThisWarehouse = false;
    };
  }, [id]);

  useEffect(() => {
    if (!user || !warehouse) return undefined;
    let isStillOnThisWarehouse = true;
    fetchWarehouseShortlist(() => isStillOnThisWarehouse);
    return () => {
      isStillOnThisWarehouse = false;
    };
  }, [warehouse, user]);

  const shipperButtonHref = `/register?redirect=/warehouse/${warehouse?.warehouseId}?contact=true&role=logistics-client&email=${email}&firstname=${firstname}&lastname=${lastname}`;

  useEffect(() => {
    if (!isContacting || !user || !warehouse) return;
    if (isWarehouseClient(user) && warehouse.isPartialProfile) {
      setIsEngageLspModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isContacting, user, warehouse]);

  if (isFetchingWarehouse) {
    return (
      <div className='flex justify-center items-center mt-48'>
        <FloxSpinner size='lg' />
      </div>
    );
  }

  if (!warehouse) {
    return (
      <div className='flex justify-center items-center mt-48'>
        <p className='text-gray-700'>No warehouse found</p>
      </div>
    );
  }

  const fleetMapping = {
    VanLessThan1AndHalft: 'Van < 1.5t',
    LgvLessThan3AndHalft: 'LDVs < 3.5t',
    TwoAxleRigidLessThan7AndHalf5t: 'Rigids < 7.5t',
    TwoAxleRigid18t: 'Rigids 18t',
    TwoAxleRigidLessThan26t: 'Rigids < 26t',
    ThreeAxleRigidLessThan26t: 'Rigids < 26t',
    MultiAxleArticMoreThan26t: 'Artics > 26t',
    SpecialOrOther: 'Special/Other',
  };

  const images = warehouse.images.map((image) => ({
    imgPath: `${WAREHOUSE_IMAGES_URL}/${image.imageLocation}/small/${image.remoteName}`,
  }));

  const maxSteps = images.length;

  const warehouseImagePlaceholder = `/images/warehouse_details_placeholder.png`;

  const hasUkwaAccreditation =
    warehouse.accreditationsAndMemberships.includes('UKWA');

  const isViewedByTheOwner = isSameBusiness(
    warehouse.userBusinessId,
    user?.businessGroupId
  );

  const matchPriceOrWarehousePrice = (
    service: keyof Match['originalRates'],
    warehousePrice?: number | null
  ) =>
    match?.serviceRates?.[service]?.acceptedValue ??
    match?.originalRates?.[service] ??
    warehousePrice;

  const priceToShow = (
    service: keyof Match['originalRates'],
    warehousePrice?: number | null
  ) =>
    formatPrice(
      matchPriceOrWarehousePrice(service, warehousePrice),
      warehouse.currency,
      '-'
    );

  const temporaryContainerStoragePrice =
    Number(
      matchPriceOrWarehousePrice(
        'containerHandlingPrice',
        warehouse.containerHandlingPrice
      ) ?? 0
    ) +
    Number(
      matchPriceOrWarehousePrice(
        'storagePricePerWeek',
        warehouse.palletStoragePricePerWeek
      ) ?? 0
    );

  const handleUpdateWarehouse = () => {
    if (!businessProfile) {
      setIsNoBusinessProfileOpen(true);
    } else if (
      !businessProfile.warehouseTandCAgreement?.hasAgreed &&
      businessProfile
    ) {
      setIsAgreeToWarehouseTandCsOpen(true);
    } else {
      navigate(`/warehouse/update/${id}`);
    }
  };

  const handleDelistWarehouse = () => {
    setIsDelistWarehouseErrorShowing(false);
    setIsDelistWarehouseModalOpen(true);
  };

  return (
    <>
      <PageHeader
        header='Warehouse'
        pages={[]}
        subHeader={
          <span className='flex items-center gap-1'>
            <MapPinIcon className='w-6' color='red' />
            {user
              ? warehouse.city
                ? warehouse.city
                : warehouse.address
              : 'Details provided upon registration'}
          </span>
        }
      >
        {user ? (
          isWarehouseClient(user) ? (
            warehouse.isPartialProfile ? (
              <Button
                label='Engage LSP'
                onClick={() => setIsEngageLspModalOpen(true)}
              />
            ) : (
              <Button
                label={
                  match?.matchStatus === 'Contracted' ||
                  match?.matchStatus === 'Reviewing T&Cs'
                    ? 'Show service rates'
                    : 'Rate negotiations'
                }
                onClick={() =>
                  navigate(`/warehouse/${warehouse.warehouseId}/service-rates`)
                }
              />
            )
          ) : (
            <div className='flex items-center gap-2'>
              <Button
                label='Delist'
                startIcon={
                  <EyeSlashIcon aria-hidden='true' className='h-5 w-5' />
                }
                onClick={handleDelistWarehouse}
              />
              <Button
                label=''
                startIcon={
                  <PencilIcon
                    aria-hidden='true'
                    className='h-5 w-5'
                    color='black'
                  />
                }
                intent='secondary'
                size='iconOnly'
                onClick={handleUpdateWarehouse}
              />
              {/* <Button
            label=''
            startIcon={
              <TrashIcon aria-hidden='true' className='h-5 w-5' color='red' />
            }
            intent='secondary'
          /> */}
            </div>
          )
        ) : (
          <a
            href={shipperButtonHref}
            target='_top'
            rel='noopener noreferrer'
            className='inline-flex items-center justify-center px-6 py-3 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 transition-colors'
          >
            Contact warehouse
          </a>
        )}
      </PageHeader>

      {/* Draft Banner */}
      {warehouse.isDraft && (
        <div className='mx-10 mt-6 p-4 bg-gray-50 border border-gray-300 rounded-xl flex items-start gap-3'>
          <div className='w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0'>
            <DocumentTextIcon className='w-5 h-5 text-gray-500' />
          </div>
          <div>
            <p className='text-sm font-semibold text-gray-800'>
              This warehouse is a draft
            </p>
            <p className='text-sm text-gray-600 mt-1'>
              This warehouse has not been submitted yet and is not visible to
              shippers. Complete all required fields and submit to make it live.
            </p>
          </div>
        </div>
      )}

      {/* Failed Vetting Banner */}
      {!warehouse.isDraft && warehouse.authenticity &&
        warehouse.authenticity.authenticityLevel !== 'High' &&
        warehouse.authenticity.authenticityLevel !== 'Very High' &&
        !warehouse.authenticity.wasSuccessfulAudit && (
          <div className='mx-10 mt-6 p-4 bg-amber-50 border border-amber-300 rounded-xl flex items-start gap-3'>
            <div className='w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0'>
              <ExclamationTriangleIcon className='w-5 h-5 text-amber-600' />
            </div>
            <div>
              <p className='text-sm font-semibold text-amber-900'>
                This warehouse is currently quarantined
              </p>
              <p className='text-sm text-amber-700 mt-1'>
                This warehouse has not yet passed our vetting process. It will
                not be visible to shippers until vetting is complete.
              </p>
            </div>
          </div>
        )}

      <div className='flex flex-col gap-8 px-10 py-6 grow font-inter'>
        <div className='flex justify-between items-center gap-8'>
          <div className='flex flex-col gap-3 w-full max-w-[400px]'>
            {images.length === 0 ? (
              <img
                className='block h-[255px] max-w-[400px] min-w-[200px] w-full overflow-hidden rounded-xl'
                src={warehouseImagePlaceholder}
                alt='Warehouse'
              />
            ) : images.length > 1 ? (
              <>
                {/* SwipeableViews replacement needed for tailwind, but keeping as-is for logic */}
                <div className='relative'>
                  {/* Images carousel */}
                  <div className='overflow-hidden'>
                    <SwipeableViews
                      enableMouseEvents
                      index={activeStep}
                      onChangeIndex={handleStepChange}
                    >
                      {images.map((step, index) => (
                        <img
                          key={index}
                          className={`block h-[255px] max-w-[400px] min-w-[200px] w-full overflow-hidden rounded-xl ${
                            index === activeStep ? '' : 'hidden'
                          }`}
                          src={step.imgPath}
                          alt={`Warehouse ${index + 1}`}
                        />
                      ))}
                    </SwipeableViews>
                  </div>
                  {/* MobileStepper */}
                  <div className='flex justify-between items-center bg-gray-100 px-2 py-1 mt-2'>
                    <button
                      className='text-gray-700 px-2 py-1 disabled:opacity-50'
                      onClick={handleBack}
                      disabled={activeStep === 0}
                      type='button'
                    >
                      <ChevronLeftIcon className='h-6 w-6' />
                    </button>
                    <span className='text-sm text-gray-500'>
                      {activeStep + 1} / {maxSteps}
                    </span>
                    <button
                      className='text-gray-700 px-2 py-1 disabled:opacity-50'
                      onClick={handleNext}
                      disabled={activeStep === maxSteps - 1}
                      type='button'
                    >
                      <ChevronRightIcon className='h-6 w-6' />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <img
                className='block h-[255px] max-w-[400px] min-w-[200px] w-full overflow-hidden rounded-xl'
                src={images[0].imgPath}
                alt='Warehouse'
              />
            )}
          </div>
          <Mapbox warehouse={warehouse} interactive={false} />
        </div>
        {warehouse.modifiedAt && (
          <div className='text-gray-500 text-sm mt-2'>
            Last updated: {moment(warehouse.modifiedAt).format('DD/MM/YYYY')}
          </div>
        )}
        <div className='flex flex-wrap items-center gap-3'>
          {hasUkwaAccreditation && (
            <img src={ukwaLogo} alt='UKWA logo' width='100' height='50' />
          )}
          {warehouse.isLspFoundingPartner && (
            <img
              src='/images/LSP%20FPC.png'
              alt='Flox LSP Founding Partners Club'
              className='h-[110px] w-auto'
            />
          )}
        </div>
        <div>{warehouse.description}</div>
        <div className='grid grid-cols-4 gap-x-4 gap-y-4'>
          <DataField
            name='Company Name'
            value={
              user
                ? warehouse.businessName ?? warehouse.userBusinessName
                : 'Details provided upon registration'
            }
          />
          <DataField
            name='Contact Name'
            value={
              user
                ? warehouse.businessContactName ?? 'N/A'
                : 'Details provided upon registration'
            }
          />
          <DataField
            name='Temperature'
            value={
              !warehouse.temperatureControlTypes.length
                ? 'N/A'
                : warehouse.temperatureControlTypes.map((type) => (
                    <span key={type}>{type}</span>
                  ))
            }
          />
          <DataField
            name='Temperature monitoring capabilities'
            value={
              !warehouse.temperatureMonitoringCapeabilities.length
                ? 'N/A'
                : warehouse.temperatureMonitoringCapeabilities.map((type) => (
                    <span key={type}>{type}</span>
                  ))
            }
          />
        </div>
        {!warehouse.isPartialProfile && (
          <>
            <Subheader
              subHeader={
                match?.matchStatus === 'Contracted' ||
                match?.matchStatus === 'Reviewing T&Cs'
                  ? 'Agreed Service Rates'
                  : 'Service Rates'
              }
            />
            <div className='grid grid-cols-4 gap-x-4 gap-y-4'>
              <DataField
                name={`Pallet Storage (per
    ${
      warehouse.storageCapacityUnitOfMeasure === 'Pallets'
        ? 'pallet-week'
        : warehouse.storageCapacityUnitOfMeasure.toLowerCase()
    }
    )`}
                value={priceToShow(
                  'storagePricePerWeek',
                  warehouse.palletStoragePricePerWeek
                )}
              />
              <DataField
                name='Pallet receipt and putaway'
                value={priceToShow(
                  'palletReceiptAndPutAwayPrice',
                  warehouse.palletReceiptAndPutAwayPrice
                )}
              />
              <DataField
                name='Pallet Picking and Despatch (per pallet)'
                value={priceToShow(
                  'palletPickingAndDespatchPrice',
                  warehouse.palletPickingAndDespatchPrice
                )}
              />
              <DataField
                name='Case Picking and Despatch (per case)'
                value={priceToShow(
                  'casePickingAndDespatchPrice',
                  warehouse.casePickingAndDespatchPrice
                )}
              />
              <DataField
                name='Item Picking and Despatch (per item)'
                value={priceToShow(
                  'itemPickingAndDespatchPrice',
                  warehouse.itemPickingAndDespatchPrice
                )}
              />
              <DataField
                name='Container Handling (per container)'
                value={priceToShow(
                  'containerHandlingPrice',
                  warehouse.containerHandlingPrice
                )}
              />
              {warehouse.warehouseOperations?.includes(
                'Temporary Container Storage'
              ) && (
                <DataField
                  name='Temporary Container Storage (per container-week)'
                  value={formatPrice(
                    temporaryContainerStoragePrice,
                    warehouse.currency
                  )}
                />
              )}
              <DataField
                name='Demurrage (per consignment day)'
                value={priceToShow(
                  'demurragePricePerDay',
                  warehouse.demurragePricePerDay
                )}
              />
              <DataField
                name='Pallet Returns (per pallet)'
                value={priceToShow(
                  'palletReturnsPrice',
                  warehouse.palletReturnsPrice
                )}
              />
              <DataField
                name='Item Returns (per item)'
                value={priceToShow(
                  'itemReturnsPrice',
                  warehouse.itemReturnsPrice
                )}
              />
              <DataField
                name='Case Returns (per case)'
                value={priceToShow(
                  'caseReturnsPrice',
                  warehouse.caseReturnsPrice
                )}
              />
              <DataField
                name='Provision of Pallet'
                value={formatPrice(
                  warehouse.provisionOfPalletPrice,
                  warehouse.currency,
                  '-'
                )}
              />
              <DataField
                name='Auxiliary services (per man-hour)'
                value={priceToShow(
                  'auxiliaryServicePricePerManHour',
                  warehouse.auxiliaryServicePricePerManHour
                )}
              />
              <DataField
                name='Additional surcharges (flat fee)'
                value={formatPrice(
                  warehouse.additionalSurchargesFlatFee,
                  warehouse.currency,
                  '-'
                )}
              />
            </div>
            {warehouse.extraInfo && (
              <div className='mt-2'>
                <div className='text-gray-400 text-sm mb-1'>Extra Info</div>
                <div className='text-gray-900 text-base whitespace-pre-wrap'>
                  {warehouse.extraInfo}
                </div>
              </div>
            )}

            {!isViewedByTheOwner && (
              <>
                <Subheader subHeader='Transaction Fees' />
                <div className='grid grid-cols-2 gap-x-4 gap-y-4'>
                  <DataField
                    name='Additional financial transaction fees'
                    value='0% - 1.75% (depending on payment option)'
                  />
                </div>
              </>
            )}
            <Subheader subHeader='Hours' />
            <div className='flex flex-col gap-4'>
              <div className='flex gap-32'>
                <div className='flex flex-col gap-4'>
                  <div className='font-semibold text-gray-900 text-base mb-2'>
                    Working hours
                  </div>
                  {warehouse.customBusinessHours.length === 0 ? (
                    'N/A'
                  ) : warehouse.standardBusinessHoursType === 'Standard24/7' ? (
                    <>
                      <DayHours day='Monday' hours='00:00 - 23:59' />
                      <DayHours day='Tuesday' hours='00:00 - 23:59' />
                      <DayHours day='Wednesday' hours='00:00 - 23:59' />
                      <DayHours day='Thursday' hours='00:00 - 23:59' />
                      <DayHours day='Friday' hours='00:00 - 23:59' />
                      <DayHours day='Saturday' hours='closed' />
                      <DayHours day='Sunday' hours='closed' />
                    </>
                  ) : (
                    <>
                      {warehouse.customBusinessHours.map((window) => (
                        <DayHours
                          key={window.day}
                          day={window.day}
                          hours={`${window.open.slice(
                            0,
                            5
                          )} - ${window.close.slice(0, 5)}`}
                        />
                      ))}
                    </>
                  )}
                </div>
                <div className='flex flex-col gap-4'>
                  <div className='font-semibold text-gray-900 text-base mb-2'>
                    Loading hours
                  </div>
                  {warehouse.vehicleLoadingTimeWindows.length === 0
                    ? 'N/A'
                    : warehouse.vehicleLoadingTimeWindows.map((window) => (
                        <DayHours
                          key={window.day}
                          day={window.day}
                          hours={`${window.open.slice(
                            0,
                            5
                          )} - ${window.close.slice(0, 5)}`}
                        />
                      ))}
                </div>
              </div>
              <DataField
                name='Despatch cut-off time'
                value={warehouse.dispatchCutoffTime?.slice(0, 5) ?? 'N/A'}
              />
              <DataField
                name='Flexible business hours'
                value={warehouse.hasFlexibleBusinessHours ? 'Yes' : 'No'}
              />
            </div>
          </>
        )}
        <Subheader subHeader='Capacity' />
        <div className='grid grid-cols-4 gap-x-4 gap-y-4'>
          <DataField
            name='Max storage capacity'
            value={`${
              warehouse.maxStorageCapacity
            } ${warehouse.storageCapacityUnitOfMeasure?.toLowerCase()}`}
          />
          <DataField
            name='Max storage capacity available'
            value={`${
              warehouse.maxStorageCapacityAvailable
            } ${warehouse.storageCapacityUnitOfMeasure?.toLowerCase()}`}
          />
          <DataField
            name='Currently available capacity for new customers'
            value={`${
              warehouse.currentlyAvailableCapacity
            } ${warehouse.storageCapacityUnitOfMeasure?.toLowerCase()}`}
          />
          <DataField
            name='Min storage capacity threshold'
            value={`${
              warehouse.minStorageCapacityThreshold
            } ${warehouse.storageCapacityUnitOfMeasure?.toLowerCase()}`}
          />
          <DataField
            name='Min storage time threshold'
            value={`${warehouse.minStorageTimeThreshold} ${
              warehouse.minStorageTimeThreshold > 1
                ? `${warehouse.storageTimeUnitOfMeasure?.toLowerCase()}s`
                : warehouse.storageTimeUnitOfMeasure?.toLowerCase()
            }`}
          />
        </div>
        {!warehouse.isPartialProfile && (
          <>
            <Subheader subHeader='Facilities' />
            <div className='flex gap-32'>
              <FacilityList
                title='Safety and Security'
                items={SAFETY_AND_SECURITY}
                available={warehouse.safetyAndSecurity}
              />
              <FacilityList
                title='Special Capabilities'
                items={STORAGE_CAPABILITIES}
                available={warehouse.storageCapabilities}
              />
              <FacilityList
                title='Material Handling Equipment'
                items={MATERIAL_HANDLING_EQUIPMENT}
                available={warehouse.materialHandlingEquipment}
              />
              <div className='w-full'>
                <FacilityList
                  title='Accreditations'
                  items={ACCREDITATIONS}
                  available={warehouse.accreditationsAndMemberships}
                />
                {warehouse.accreditationsAndMemberships.includes('Other') &&
                  warehouse.accreditationOther && (
                    <div className='mt-3 ml-7 text-sm text-gray-700'>
                      <span className='text-gray-400'>Other: </span>
                      {warehouse.accreditationOther}
                    </div>
                  )}
              </div>
            </div>
            <Subheader subHeader='Services' />
            <div className='flex gap-32'>
              <FacilityList
                title='Warehouse Operations'
                items={WAREHOUSE_OPERATIONS}
                available={warehouse.warehouseOperations}
              />
              <FacilityList
                title='Additional Services'
                items={ADDITIONAL_SERVICES}
                available={warehouse.additionalServices}
              />
              <FacilityList
                title='Fulfilment Services'
                items={FULFILMENT_SERVICES}
                available={warehouse.fulfilmentServices}
              />
            </div>
            <Subheader subHeader='Specifications' />
            <div className='flex flex-col items-start font-inter mb-4'>
              <span className='text-gray-900 text-base font-medium mb-1'>
                {warehouse.sectorExperience.length === 0
                  ? 'N/A'
                  : warehouse.sectorExperience.join(', ')}
              </span>
              <span className='text-gray-400 text-sm'>Sector experience</span>
            </div>
            <div className='grid grid-cols-4 gap-x-4 gap-y-4'>
              <DataField
                name='Construction year'
                value={warehouse.constructionYear || 'N/A'}
              />
              <DataField
                name='Headcount'
                value={`${warehouse.minHeadcount || 'N/A'} - ${
                  warehouse.maxHeadcount || 'N/A'
                }`}
              />
              <DataField
                name='No/ of shifts'
                value={warehouse.numberOfShifts || 'N/A'}
              />
              <DataField
                name='Onsite office'
                value={warehouse.hasOnsiteOffice ? 'Yes' : 'No'}
              />
              <DataField
                name='Off dock'
                value={warehouse.isOffDock ? 'Yes' : 'No'}
              />
              <DataField
                name='Is quay'
                value={warehouse.isQuay ? 'Yes' : 'No'}
              />
              <DataField
                name='Racking available'
                value={warehouse.isRackingAvailable ? 'Yes' : 'No'}
              />
              {warehouse.isRackingAvailable && (
                <>
                  <DataField
                    name='Racking width'
                    value={warehouse.individualRackWidth || 'N/A'}
                  />
                  <DataField
                    name='Racking height'
                    value={warehouse.individualRackHeight || 'N/A'}
                  />
                  <DataField
                    name='Racking depth'
                    value={warehouse.individualRackDepth || 'N/A'}
                  />
                  <DataField
                    name='Total rack height'
                    value={warehouse.totalRackHeight || 'N/A'}
                  />
                </>
              )}
              <DataField
                name='GIA (Gross Internal Area)'
                value={
                  warehouse.grossInternalArea?.quantity
                    ? `${parseFloat(warehouse.grossInternalArea.quantity.toFixed(2))} ${warehouse.grossInternalArea.unitOfMeasure}`
                    : 'N/A'
                }
              />
              <DataField
                name='Minimum eaves height'
                value={
                  warehouse.minEavesHeight?.quantity
                    ? `${parseFloat(warehouse.minEavesHeight.quantity.toFixed(2))} ${warehouse.minEavesHeight.unitOfMeasure}`
                    : 'N/A'
                }
              />
              <DataField
                name='Vehicle access height'
                value={
                  warehouse.vehicleAccessHeight?.quantity
                    ? `${parseFloat(warehouse.vehicleAccessHeight.quantity.toFixed(2))} ${warehouse.vehicleAccessHeight.unitOfMeasure}`
                    : 'N/A'
                }
              />
              <DataField
                name='Yard space'
                value={
                  warehouse.yardSpace?.quantity
                    ? `${parseFloat(warehouse.yardSpace.quantity.toFixed(2))} ${warehouse.yardSpace.unitOfMeasure}`
                    : 'N/A'
                }
              />
              <DataField
                name='Mezzanine area'
                value={
                  warehouse.mezzanineArea?.quantity
                    ? `${parseFloat(warehouse.mezzanineArea.quantity.toFixed(2))} ${warehouse.mezzanineArea.unitOfMeasure}`
                    : 'N/A'
                }
              />
              <DataField
                name='No/ of loading docks'
                value={warehouse.loadingDocks || 'N/A'}
              />
              <DataField
                name='No/ of lorry parking spaces'
                value={warehouse.lorryParkingSpaces || 'N/A'}
              />
              <DataField
                name='No/ of car parking spaces'
                value={warehouse.carParkingSpaces || 'N/A'}
              />
              <DataField
                name='No/ of double height loading docks'
                value={warehouse.doubleHeightLoadingDocks || 'N/A'}
              />
              <DataField
                name='No/ of levelled loading docks'
                value={warehouse.leveledLoadingDocks || 'N/A'}
              />
              <DataField
                name='Systems available'
                value={
                  warehouse.systemsAvailable.length === 0
                    ? 'N/A'
                    : warehouse.systemsAvailable.length === 1
                    ? warehouse.systemsAvailable[0]
                    : warehouse.systemsAvailable.length === 2
                    ? `${warehouse.systemsAvailable[0]} and ${warehouse.systemsAvailable[1]}`
                    : [
                        warehouse.systemsAvailable.slice(0, -1).join(', '),
                        ' and ',
                        warehouse.systemsAvailable[
                          warehouse.systemsAvailable.length - 1
                        ],
                      ].join('')
                }
              />
            </div>
            {Array.isArray(warehouse.fleet) &&
              warehouse.fleet.some((f) => f.count && f.count > 0) && (
                <>
                  <Subheader subHeader='Fleet' />
                  <div className='grid grid-cols-4 gap-x-4 gap-y-4'>
                    {warehouse.fleet.map((fleetItem) => (
                      <DataField
                        key={fleetItem.type}
                        name={
                          fleetMapping[fleetItem.type as Fleet] ||
                          fleetItem.type
                        }
                        value={fleetItem.count}
                      />
                    ))}
                  </div>
                </>
              )}
          </>
        )}
      </div>
      <Modal
        open={isNoBusinessProfileOpen}
        onOpenChange={(isOpen: boolean) => {
          setIsNoBusinessProfileOpen(isOpen);
        }}
      >
        <ModalContainer preventCloseOnOutsideClick style={{ zIndex: 9999 }}>
          <ModalHeader title='Action Required: Finalise Your Corporate Details' />
          <ModalContent>
            <p className='text-gray-700'>
              To complete the Price Schedule agreement, we need you to finalise
              your corporate details. Please update your Business Profile by
              clicking the button below.
            </p>
          </ModalContent>
          <ModalFooter>
            <Button
              label='Take me to my Business Profile'
              onClick={() =>
                navigate(`/profile?warehouseID=${warehouse.warehouseId}`)
              }
            />
          </ModalFooter>
        </ModalContainer>
      </Modal>
      <Modal
        open={isDelistWarehouseModalOpen}
        onOpenChange={(isOpen: boolean) => {
          setIsDelistWarehouseModalOpen(isOpen);
        }}
      >
        <ModalContainer style={{ zIndex: 9999 }}>
          <ModalHeader title='Delist Warehouse' />
          <ModalContent>
            <p className='text-gray-700'>
              Are you sure you want to delist this warehouse? This action will
              prevent customers from searching for this warehouse and will
              require administrator action to undo.
            </p>
            {isDelistWarehouseErrorShowing && (
              <p className='text-red-600'>
                Something went wrong. Please try again.
              </p>
            )}
          </ModalContent>
          <ModalFooter>
            <Button label='Delist' onClick={delistWarehouse} />
          </ModalFooter>
        </ModalContainer>
      </Modal>
      <AgreeToWarehouseTandCsModal
        isAgreeToWarehouseTandCsOpen={isAgreeToWarehouseTandCsOpen}
        setIsAgreeToWarehouseTandCsOpen={setIsAgreeToWarehouseTandCsOpen}
      />
      <EngageLspModal
        open={isEngageLspModalOpen}
        onOpenChange={setIsEngageLspModalOpen}
        warehouseId={warehouse.warehouseId}
        warehouseName={warehouse.name}
      />
    </>
  );
}
